import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Button } from '@/components/ui/button'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import { useBudgetMutations } from '../hooks/useBudgetMutations'
import { visibleColumns, type ValueKey } from '../lib/columns'
import { costBudgetDownloadUrl } from '../lib/download'
import { commitInput, nextEditingCell, type CellMove, type CommitValue } from '../lib/editing'
import { describeCostControlError } from '../lib/errors'
import { moneyLocale } from '../lib/money'
import { budgetAffordances, type EditableColumn } from '../lib/permissions'
import { recordPath } from '../lib/records'
import { budgetRows, buildTree, collapsibleIds, siblingPosition } from '../lib/tree'
import { BudgetGridStoreContext, createBudgetGridStore, useBudgetGridStore } from '../store/budgetGridStore'
import type { CostControlView, CostRow } from '../types'
import BreakdownSheet, { type BreakdownTarget } from './BreakdownSheet'
import BudgetActions from './BudgetActions'
import BudgetEmptyState from './BudgetEmptyState'
import BudgetSummary from './BudgetSummary'
import BudgetTabSkeleton from './BudgetTabSkeleton'
import BudgetToolbar, { type BudgetViewMode } from './BudgetToolbar'
import ByContractView from './ByContractView'
import BudgetTreeGrid from './BudgetTreeGrid'
import GroupEstimateDialog from './GroupEstimateDialog'
import ImportDialog from './ImportDialog'
import MoveNodeDialog from './MoveNodeDialog'
import NodeHistorySheet from './NodeHistorySheet'
import NodeDialog, { type NodeDialogState } from './NodeDialog'
import type { RowAction } from './RowMenu'
import SettingsDialog from './SettingsDialog'

interface Props { projectId: string }

const BudgetTabContent: React.FC<Props> = ({ projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { history, location } = useRouter()
  const viewMode: BudgetViewMode = new URLSearchParams(location.search).get('view') === 'contracts' ? 'contracts' : 'budget'
  const setViewMode = (mode: BudgetViewMode) => {
    const params = new URLSearchParams(location.search)
    if (mode === 'contracts') params.set('view', 'contracts')
    else params.delete('view')
    const qs = params.toString()
    history.replace(`${location.pathname}${qs ? `?${qs}` : ''}`)
  }
  const actions = useModuleActions('COST_CONTROL', projectId)
  const docControl = useModuleActions('DOC_CONTROL', projectId)
  const { data, loading, error, refetch } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const view = data?.costControl

  const collapsedIds = useBudgetGridStore((s) => s.collapsedIds)
  const columnVisibility = useBudgetGridStore((s) => s.columnVisibility)
  const storedRootId = useBudgetGridStore((s) => s.rootId)
  const search = useBudgetGridStore((s) => s.search)
  const toggleCollapsed = useBudgetGridStore((s) => s.toggleCollapsed)
  const setCollapsed = useBudgetGridStore((s) => s.setCollapsed)
  const toggleColumn = useBudgetGridStore((s) => s.toggleColumn)
  const setRootId = useBudgetGridStore((s) => s.setRootId)
  const setSearch = useBudgetGridStore((s) => s.setSearch)
  const editing = useBudgetGridStore((s) => s.editing)
  const setEditing = useBudgetGridStore((s) => s.setEditing)
  const gridStore = useContext(BudgetGridStoreContext)
  const mutations = useBudgetMutations(projectId)

  const tree = useMemo(() => buildTree(view?.nodes ?? []), [view])
  const rootId = storedRootId && tree.byId.get(storedRootId)?.activity.parentId === null ? storedRootId : null
  const collapsed = useMemo(() => new Set(collapsedIds), [collapsedIds])
  const rows = useMemo(() => budgetRows(tree, collapsed, { rootId, search }), [tree, collapsed, rootId, search])
  const columns = useMemo(() => visibleColumns(columnVisibility, !!view?.hasInvestorContract), [columnVisibility, view?.hasInvestorContract])
  const levelOne = useMemo(() => tree.roots.map((r) => ({ id: r.activity.id, name: r.activity.name })), [tree])

  const locked = !!view?.settings.budgetLockedAt
  const { canView, canCreate, canEdit, canDelete, canApprove, canExport, canAdmin } = actions
  const affordances = useMemo(
    () => budgetAffordances({ canView, canCreate, canEdit, canDelete, canApprove, canExport, canAdmin }, locked),
    [canView, canCreate, canEdit, canDelete, canApprove, canExport, canAdmin, locked],
  )
  const visible = useMemo(() => new Set(columns), [columns])

  // Callbacks handed to memoized rows must keep their identity; they read the latest render through this ref.
  const latest = useRef({ rows, tree, affordances, visible, mutations })
  latest.current = { rows, tree, affordances, visible, mutations }

  const onStartEdit = useCallback((nodeId: string, column: EditableColumn) => setEditing({ nodeId, column }), [setEditing])
  const onCancelEdit = useCallback(() => setEditing(null), [setEditing])
  const onCommit = useCallback((nodeId: string, value: CommitValue, move: CellMove) => {
    const { rows: rs, tree: tr, affordances: af, visible: vis, mutations: m } = latest.current
    if (move !== 'stay') {
      setEditing(nextEditingCell(rs, nodeId, value.column, move, af, vis))
    } else {
      // A late blur / unmount commit must not close an editor that has already moved to another cell.
      const current = gridStore?.getState().editing
      if (current && current.nodeId === nodeId && current.column === value.column) setEditing(null)
    }
    const node = tr.byId.get(nodeId)?.activity
    const input = node ? commitInput(node, value) : null
    if (input) void m.updateNode(nodeId, input)
  }, [setEditing, gridStore])

  const [breakdown, setBreakdown] = useState<BreakdownTarget | null>(null)
  const onOpenCell = useCallback((nodeId: string, key: ValueKey) => setBreakdown({ nodeId, key }), [])

  const [nodeDialog, setNodeDialog] = useState<NodeDialogState | null>(null)
  const [moveId, setMoveId] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<CostRow | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [lockDialog, setLockDialog] = useState<'lock' | 'unlock' | null>(null)
  const [lockSubmitting, setLockSubmitting] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [historyNode, setHistoryNode] = useState<CostRow | null>(null)
  const [estimateNode, setEstimateNode] = useState<CostRow | null>(null)

  const onRowAction = useCallback((nodeId: string, action: RowAction) => {
    const { tree: tr, mutations: m } = latest.current
    const row = tr.byId.get(nodeId)?.activity
    if (!row) return
    switch (action.kind) {
      case 'addGroup': setNodeDialog({ kind: 'GROUP', parentId: row.id }); break
      case 'addItem': setNodeDialog(row.kind === 'GROUP' ? { kind: 'ITEM', parentId: row.id } : { kind: 'ITEM', parentId: row.parentId, afterId: row.id }); break
      case 'rename': setEditing({ nodeId, column: 'name' }); break
      case 'editBudget':
        if (!latest.current.visible.has('originalBudget')) toggleColumn('originalBudget')
        setEditing({ nodeId, column: 'originalBudget' })
        break
      case 'moveUp': void m.moveNode(nodeId, row.parentId, siblingPosition(tr, nodeId).index - 1); break
      case 'moveDown': void m.moveNode(nodeId, row.parentId, siblingPosition(tr, nodeId).index + 1); break
      case 'moveTo': setMoveId(nodeId); break
      case 'linkBuilding': void m.updateNode(nodeId, action.buildingId ? { buildingId: action.buildingId } : { clearBuilding: true }); break
      case 'clearForecast': void m.updateNode(nodeId, { clearForecastOverride: true }); break
      case 'groupEstimate': setEstimateNode(row); break
      case 'delete': setPendingDelete(row); break
      case 'history': setHistoryNode(row); break
    }
  }, [setEditing, toggleColumn])

  const onCreate = useCallback(async (input: Record<string, unknown>) => {
    const ok = await latest.current.mutations.createNode(input)
    // A new row under a collapsed group would be invisible.
    if (ok && typeof input.parentId === 'string') setCollapsed([input.parentId], false)
    return ok
  }, [setCollapsed])

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    const ok = await mutations.deleteNode(pendingDelete.id)
    setDeleting(false)
    setPendingDelete(null)
    if (ok) toastr.success('', t('CostControl_deleted'))
  }

  const confirmLock = async () => {
    if (!lockDialog) return
    setLockSubmitting(true)
    const ok = lockDialog === 'lock' ? await mutations.lock() : await mutations.unlock()
    setLockSubmitting(false)
    if (ok) toastr.success('', t(lockDialog === 'lock' ? 'CostControl_locked_toast' : 'CostControl_unlocked_toast'))
    setLockDialog(null)
  }
  const exportBudget = (format: 'xlsx' | 'csv') => { window.open(costBudgetDownloadUrl(projectId, format, rootId), '_blank') }
  const addLevelOne = () => setNodeDialog({ kind: 'GROUP', parentId: null })

  if (!view && loading) return <BudgetTabSkeleton />
  if (!view) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-sm text-muted-foreground">
        <p>{describeCostControlError(error?.message, t)}</p>
        <Button variant="outline" size="sm" className="h-8" onClick={() => { refetch().catch(() => undefined) }}>{t('CostControl_retry')}</Button>
      </div>
    )
  }

  const currency = view.settings.currency
  const scope = rootId ? tree.byId.get(rootId)!.activity.values : view.total
  return (
    <div>
      {viewMode === 'budget' && view.nodes.length > 0 && <BudgetSummary scope={scope} currency={currency} locale={locale} />}
      <BudgetToolbar
        levelOne={levelOne}
        rootId={rootId}
        onRootChange={setRootId}
        search={search}
        onSearchChange={setSearch}
        saving={mutations.saving}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        actions={(
          <BudgetActions
            affordances={affordances}
            lockedAt={view.settings.budgetLockedAt}
            locale={locale}
            rootName={rootId ? tree.byId.get(rootId)?.activity.name ?? null : null}
            onAddLevelOne={addLevelOne}
            onImport={() => setImportOpen(true)}
            onExport={exportBudget}
            onLock={() => setLockDialog('lock')}
            onUnlock={() => setLockDialog('unlock')}
            onSettings={() => setSettingsOpen(true)}
          />
        )}
      />
      {viewMode === 'contracts' ? (
        <ByContractView projectId={projectId} currency={currency} locale={locale} />
      ) : view.nodes.length === 0 ? (
        <BudgetEmptyState onImport={affordances.importBudget ? () => setImportOpen(true) : undefined} onAddGroup={affordances.addNode ? addLevelOne : undefined} />
      ) : (
        <>
          <BudgetTreeGrid
            rows={rows}
            columns={columns}
            hasInvestorContract={!!view.hasInvestorContract}
            visibility={columnVisibility}
            currency={currency}
            locale={locale}
            footerLabel={t(rootId ? 'CostControl_total_level_one' : 'CostControl_total_project')}
            footerValues={scope}
            affordances={affordances}
            visible={visible}
            tree={tree}
            buildings={view.buildings}
            editing={editing}
            onToggle={toggleCollapsed}
            onToggleColumn={toggleColumn}
            onCollapseAll={() => setCollapsed(collapsibleIds(tree), true)}
            onExpandAll={() => setCollapsed(collapsibleIds(tree), false)}
            onOpenCell={onOpenCell}
            onStartEdit={onStartEdit}
            onCommit={onCommit}
            onCancelEdit={onCancelEdit}
            onRowAction={onRowAction}
          />
          {rows.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">{t('CostControl_no_matches')}</p>}
        </>
      )}
      <NodeDialog state={nodeDialog} locked={locked} buildings={view.buildings} onClose={() => setNodeDialog(null)} onCreate={onCreate} />
      <MoveNodeDialog nodeId={moveId} tree={tree} onClose={() => setMoveId(null)} onMove={mutations.moveNode} />
      <ConfirmDialog
        open={!!pendingDelete}
        title={t('CostControl_delete_title')}
        description={pendingDelete ? t(pendingDelete.kind === 'GROUP' ? 'CostControl_delete_group_body' : 'CostControl_delete_item_body', { name: pendingDelete.name }) : undefined}
        confirmLabel={t('Common_delete')}
        variant="destructive"
        submitting={deleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => { void confirmDelete() }}
      />
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={view.settings}
        levelOne={tree.roots.map((r) => r.activity)}
        buildings={view.buildings}
        affordances={affordances}
        onSaveCurrency={mutations.updateSettings}
        onSaveThreshold={mutations.updateThreshold}
        onLinkBuilding={(nodeId, buildingId) => mutations.updateNode(nodeId, buildingId ? { buildingId } : { clearBuilding: true })}
      />
      <ConfirmDialog
        open={lockDialog !== null}
        title={t(lockDialog === 'unlock' ? 'CostControl_unlock_title' : 'CostControl_lock_title')}
        description={t(lockDialog === 'unlock' ? 'CostControl_unlock_body' : 'CostControl_lock_body')}
        confirmLabel={t(lockDialog === 'unlock' ? 'CostControl_unlock' : 'CostControl_lock')}
        submitting={lockSubmitting}
        onCancel={() => setLockDialog(null)}
        onConfirm={() => { void confirmLock() }}
      />
      <BreakdownSheet
        projectId={projectId}
        target={breakdown}
        tree={tree}
        currency={currency}
        locale={locale}
        onNavigate={(key) => setBreakdown((b) => (b ? { ...b, key } : b))}
        onClose={() => setBreakdown(null)}
        onOpenRecord={(sourceType, id) => { const path = recordPath(projectId, sourceType, id); if (path) history.push(path) }}
      />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} projectId={projectId} locked={locked} canLinkDocuments={docControl.canView} />
      <GroupEstimateDialog
        open={!!estimateNode}
        row={estimateNode}
        rows={view.nodes}
        currency={currency}
        locale={locale}
        onClose={() => setEstimateNode(null)}
        onSave={async (amount) => {
          if (estimateNode && await mutations.setGroupEstimate(estimateNode.id, amount)) setEstimateNode(null)
        }}
      />
      <NodeHistorySheet projectId={projectId} node={historyNode} currency={currency} locale={locale} onClose={() => setHistoryNode(null)} />
    </div>
  )
}

const BudgetTab: React.FC<Props> = ({ projectId }) => {
  const store = useMemo(() => createBudgetGridStore(projectId), [projectId])
  return (
    <BudgetGridStoreContext.Provider value={store}>
      <BudgetTabContent projectId={projectId} />
    </BudgetGridStoreContext.Provider>
  )
}
export default BudgetTab
